import { createSelector } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { blotterSlice } from '@/store/blotter/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import { isCallOrder } from '@/store/orders/call/helpers.ts';
import type { ExpiryDate, GoodTillCancelled } from '@/store/orders/call/state.ts';
import type { Order } from '@/store/orders/state.ts';
import type { AppState } from '@/store/store.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectCallOrderTile = createSelector(
  (state: AppState, tileId: string) => state.orders[tileId] as Order | undefined,
  (order) => (isCallOrder(order) ? order : undefined),
);

export const selectOrderEntryByIdFromBlotter = createSelector(
  (state: AppState, orderId: string) => blotterSlice.selectors.getOrderEntryById(state, orderId),
  (order) => order,
);

export const selectCallOrderErrors = createSelector(
  (state: AppState, tileId: string) => selectCallOrderTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'CallOrder') {
      return undefined;
    }

    return order.properties.errors;
  },
);

export const selectExpiryCallOrder = createSelector(
  (state: AppState, tileId: string) => selectCallOrderTile(state, tileId),
  (order): GoodTillCancelled | ExpiryDate | undefined => {
    if (isNotDefined(order) || order.type !== 'CallOrder') {
      return undefined;
    }

    return order.properties.expiry;
  },
);

export const selectTradeIdFromCallOrderTile = createSelector(
  (state: AppState, tileId: string) => selectCallOrderTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (order, tileId) => {
    if (isNotDefined(order) || order.type !== 'CallOrder') {
      return undefined;
    }

    if (
      order.properties.status.type === 'creating' ||
      order.properties.status.type === 'failed' ||
      order.properties.status.type === 'pending'
    ) {
      console.warn(`[selectTradeIdFromCallOrderTile] Tile with id ${tileId} is in creating state, no data available.`);
      return undefined;
    }

    return order.properties.status.tradeId;
  },
);

export const selectLimitPriceCallOrder = createSelector(
  (state: AppState, tileId: string) => selectCallOrderTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'CallOrder') {
      return undefined;
    }

    return order.properties.limitPrice;
  },
);

export const selectNotificationEmailsCallOrder = createSelector(
  (state: AppState, tileId: string) => selectCallOrderTile(state, tileId),
  (state: AppState) => state.userPreferences,
  (order, userPreference) => {
    if (isNotDefined(order) || order.type !== 'CallOrder') {
      return [];
    }

    if (isDefined(order.properties.notificationEmails)) {
      return order.properties.notificationEmails;
    }

    return userPreference.splitNotifications ? userPreference.splitNotificationsEmailsOrders : userPreference.emails;
  },
);

export const selectSpotCallOrderData = createSelector(
  (state: AppState, tileId: string) => selectCallOrderTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (state: AppState, tileId: string) => {
    const order = selectCallOrderTile(state, tileId);

    if (
      isNotDefined(order) ||
      order.type !== 'CallOrder' ||
      isNotDefined(order.properties.currencyPair?.tiering) ||
      isNotDefined(order.properties.currencyPair?.currency1) ||
      isNotDefined(order.properties.currencyPair?.currency2)
    ) {
      return undefined;
    }

    const currencyPair = `${order.properties.currencyPair.currency1}/${order.properties.currencyPair.currency2}`;
    const espStreamId = getEspStreamId(currencyPair, order.properties.currencyPair.tiering);

    return state.esp.streams[espStreamId];
  },
  (order, tileId, espPrice) => {
    if (isNotDefined(order) || order.type !== 'CallOrder') {
      return undefined;
    }

    if (isNotDefined(order.properties.currencyPair?.tiering)) {
      console.warn(`[CallOrder] Tile with id ${tileId} has no tiering defined.`);
      return undefined;
    }

    return {
      spot:
        espPrice?.status === 'PRICED'
          ? convertPointsToRate(espPrice.midRate, espPrice.currencyPairPrecision)
          : undefined,
      digitCount: espPrice?.status === 'PRICED' ? espPrice.currencyPairPrecision - 1 : undefined,
    };
  },
);
